import React, { useEffect, useRef } from 'react';
import { Application, Container } from 'pixi.js';
import type { PublicPlayerAppearance } from '@push.io/shared';
import { AssetManager } from '../../game/cosmetics/AssetManager.js';
import { PlayerView } from '../../game/cosmetics/PlayerView.js';
import { GraphicsLevel, selectProfile } from '../../game/cosmetics/graphics-quality.js';

export function PlayerPreview({ appearance, playerName, reducedMotion = false }: {
  appearance: PublicPlayerAppearance;
  playerName: string;
  reducedMotion?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const appearanceRef = useRef<PublicPlayerAppearance>(appearance);
  const playerNameRef = useRef(playerName);
  const reducedMotionRef = useRef(reducedMotion);

  useEffect(() => {
    appearanceRef.current = appearance;
  }, [appearance]);

  useEffect(() => {
    playerNameRef.current = playerName;
  }, [playerName]);

  useEffect(() => {
    reducedMotionRef.current = reducedMotion;
  }, [reducedMotion]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const app = new Application();
    const root = new Container();
    const view = new PlayerView('preview-player');
    const profile = selectProfile(GraphicsLevel.MEDIUM);
    let mounted = true;
    let initialized = false;
    let cleaned = false;
    let rafId = 0;
    let elapsed = 0;

    const resize = () => {
      if (!initialized) return;
      const width = host.clientWidth || 320;
      const height = host.clientHeight || 320;
      app.renderer.resize(width, height);
    };

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      mounted = false;
      window.removeEventListener('resize', onResize);
      window.cancelAnimationFrame(rafId);

      try {
        app.stop();
      } catch {}

      try {
        if (initialized) app.stage.removeChild(root);
      } catch {}

      try {
        view.destroy();
      } catch {}

      try {
        root.destroy({ children: true });
      } catch {}

      try {
        if (initialized) app.destroy();
      } catch {}

      host.textContent = '';
    };

    void (async () => {
      await app.init({
        width: host.clientWidth || 320,
        height: host.clientHeight || 320,
        antialias: true,
        backgroundAlpha: 0,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        autoDensity: true,
      });
      initialized = true;
      if (!mounted) {
        cleanup();
        return;
      }

      host.appendChild(app.canvas as HTMLCanvasElement);
      app.stage.addChild(root);
      root.addChild(view.root);
      await AssetManager.initialize().catch(() => undefined);
      view.setQuality(profile);

      const animate = () => {
        if (!mounted) return;
        elapsed += 16;
        resize();
        const width = host.clientWidth || 320;
        const height = host.clientHeight || 320;
        const reduced = reducedMotionRef.current;
        const currentAppearance = appearanceRef.current;
        const currentPlayerName = playerNameRef.current;

        const bob = reduced ? 0 : Math.sin(elapsed * 0.0022) * 8;
        const drift = reduced ? 0 : Math.sin(elapsed * 0.0018) * 22;
        const vx = reduced ? 0 : Math.cos(elapsed * 0.0036) * 2.6;
        const vy = reduced ? 0 : Math.sin(elapsed * 0.0028) * 1.4;

        view.updateLayers(currentAppearance, Math.min(width, height) * 0.17, false, currentPlayerName);
        view.root.position.set(width * 0.5 + drift, height * 0.5 + bob);
        view.updateTrail(16, width * 0.5 + drift, height * 0.5 + bob, vx, vy, currentAppearance, profile);

        rafId = window.requestAnimationFrame(animate);
      };

      animate();
    })();

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    return cleanup;
  }, []);

  return <div ref={hostRef} />;
}
